"use client"

import * as React from "react"
import { ChevronDown, X, Search, ListChecks } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useSearchMode, fuzzyMatch, type SearchMode } from "./use-search-mode"
import { VIEW_FILTER_ALL, isAllSelected } from "@/lib/view-preferences/filter-sentinel"

/** Above this count, the popover auto-opens in "Selected only" review mode. */
const LARGE_SELECTION_THRESHOLD = 50

export interface FilterOption {
  value: string
  label: string
  /** Count badge shown next to label (e.g. "(12)"). When 0, option is disabled with tooltip. */
  count?: number
  /** Explicit disabled flag — takes precedence over count check */
  disabled?: boolean
}

interface MultiSelectFilterProps {
  title: string
  options: FilterOption[]
  selectedValues: string[]
  onSelectionChange: (values: string[]) => void
  /** Show search input when option count exceeds this threshold. Default: 0 (always show) */
  searchThreshold?: number
  /** Tooltip description shown on hover when no values are selected */
  description?: string
  /** Hide the Exact/Fuzzy search mode toggle (e.g. for enum filters where it is meaningless) */
  hideSearchMode?: boolean
}

export function MultiSelectFilter({
  title,
  options,
  selectedValues,
  onSelectionChange,
  searchThreshold = 0,
  description,
  hideSearchMode = false,
}: MultiSelectFilterProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")
  const [selectedOnly, setSelectedOnly] = React.useState(false)
  const [searchMode, setSearchMode] = useSearchMode()

  const showSearch = options.length > searchThreshold

  // "Select All" collapses the selection to the VIEW_FILTER_ALL sentinel
  // (one token instead of N enumerated values). Everything below treats the
  // sentinel as "every option selected" via `effectiveSelected`.
  const isAll = isAllSelected(selectedValues)
  const allOptionValues = React.useMemo(() => options.map(o => o.value), [options])
  // Mirror the per-option disabled rule used at the row render (count===0 OR
  // explicit disabled). Select All / sentinel-collapse logic must NOT pick
  // up zero-count buckets — otherwise the chip says "Status: All" while
  // half the underlying statuses have no data, and "Select All" appears to
  // toggle options the user couldn't click individually.
  const enabledOptionValues = React.useMemo(
    () =>
      options
        .filter(o => !o.disabled && o.count !== 0)
        .map(o => o.value),
    [options]
  )
  const effectiveSelected = React.useMemo(
    () => (isAll ? new Set(allOptionValues) : new Set(selectedValues)),
    [isAll, allOptionValues, selectedValues]
  )
  const effectiveCount = isAll ? options.length : selectedValues.length
  const isLargeSelection = effectiveCount > LARGE_SELECTION_THRESHOLD

  const selectedLabelList = React.useMemo(
    () =>
      isAll
        ? options.map(o => o.label)
        : selectedValues.map(v => options.find(o => o.value === v)?.label ?? v),
    [isAll, selectedValues, options]
  )

  const useTwoColumns = selectedLabelList.length > 12
  const TOOLTIP_PREVIEW_MAX = 24
  const previewLabels = selectedLabelList.slice(0, TOOLTIP_PREVIEW_MAX)
  const hiddenPreviewCount = selectedLabelList.length - previewLabels.length

  const filtered = React.useMemo(() => {
    let list = options
    if (selectedOnly) {
      list = list.filter(o => effectiveSelected.has(o.value))
    }
    const q = search.trim()
    if (showSearch && q) {
      if (searchMode === "fuzzy") {
        list = list.filter(o => fuzzyMatch(q, o.label))
      } else {
        const tokens = q.toLowerCase().split(/\s+/).filter(Boolean)
        list = list.filter(o => {
          const words = o.label.toLowerCase().split(/[\s\-/,()]+/).filter(Boolean)
          return tokens.every(token => words.some(word => word.startsWith(token)))
        })
      }
    }
    return list
  }, [options, selectedOnly, effectiveSelected, showSearch, search, searchMode])

  const handleToggle = (value: string) => {
    if (isAll) {
      // Deselecting a value from a full "Select All": expand the sentinel
      // back to the explicit list minus this value. There is no exclude
      // model — once you deselect, the facet is enumerated again.
      onSelectionChange(allOptionValues.filter(v => v !== value))
      return
    }
    if (selectedValues.includes(value)) {
      onSelectionChange(selectedValues.filter(v => v !== value))
    } else {
      onSelectionChange([...selectedValues, value])
    }
  }

  /**
   * Toggle the "Select All" row. When the resulting selection covers every
   * option, collapse it to the VIEW_FILTER_ALL sentinel so the URL / GET /
   * view-preferences row never enumerate hundreds of values.
   */
  const handleSelectAll = () => {
    // Only act on options that are actually selectable. Zero-count
    // ("Bounced (0)") or explicitly disabled rows would otherwise sneak
    // into the selection here even though the row checkbox is locked.
    const filteredValues = filtered
      .filter(o => !o.disabled && o.count !== 0)
      .map(o => o.value)
    const allFilteredSelected = filteredValues.every(v => effectiveSelected.has(v))
    const base = isAll ? allOptionValues : selectedValues
    if (allFilteredSelected) {
      onSelectionChange(base.filter(v => !filteredValues.includes(v)))
      return
    }
    const merged = new Set([...base, ...filteredValues])
    // Collapse to the "All" sentinel only when every ENABLED option is
    // covered — otherwise the chip would say "Status: All" while two of
    // the four statuses can't even be selected by the user.
    const coversEverything =
      enabledOptionValues.length > 0 && enabledOptionValues.every(v => merged.has(v))
    onSelectionChange(coversEverything ? [VIEW_FILTER_ALL] : Array.from(merged))
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelectionChange([])
  }

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (isOpen) {
      setSelectedOnly(isLargeSelection)
    } else {
      setSearch("")
      setSelectedOnly(false)
    }
  }

  return (
    <TooltipProvider>
      <Tooltip open={open || selectedValues.length === 0 ? false : undefined}>
    <Popover open={open} onOpenChange={handleOpenChange}>
      <TooltipTrigger asChild>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 w-auto min-w-fit max-w-[260px] border-dashed">
          {selectedValues.length > 0 ? (
            <>
              {/* #658/#659 — show the selected value(s), not just a count.
                  1 picked → "<Title>: <label>"
                  2 picked → "<Title>: <a>, <b>"
                  3+ picked or isAll → "<Title>: <a> +N" / "<Title>: All" */}
              <span className="mr-1 shrink-0">{title}:</span>
              <span className="truncate">
                {isAll
                  ? "All"
                  : selectedLabelList.length === 1
                    ? selectedLabelList[0]
                    : selectedLabelList.length === 2
                      ? selectedLabelList.join(", ")
                      : `${selectedLabelList[0]} +${selectedLabelList.length - 1}`}
              </span>
              <span
                role="button"
                title={`Clear ${title} filter`}
                onClick={handleClear}
                className="ml-1 shrink-0 rounded-sm opacity-70 hover:opacity-100 hover:bg-muted p-0.5"
              >
                <X className="h-3 w-3" />
              </span>
            </>
          ) : (
            <>
              <span>{title}</span>
              <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
            </>
          )}
        </Button>
      </PopoverTrigger>
      </TooltipTrigger>
      <PopoverContent
        className={`p-2 ${selectedOnly ? "w-[400px]" : "w-[320px]"}`}
        align="start"
      >
        {showSearch && (
          <div className="flex items-center gap-1.5 mb-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder={
                  selectedOnly
                    ? `Search selected ${title.toLowerCase()}...`
                    : `Search ${title.toLowerCase()}...`
                }
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="h-8 pl-7 text-sm"
                autoFocus
              />
            </div>
            {!hideSearchMode && <SearchModeToggle mode={searchMode} onChange={setSearchMode} />}
          </div>
        )}
        {selectedValues.length > 0 && (
          <div className="flex items-center justify-between mb-2 px-1">
            <button
              type="button"
              onClick={() => {
                setSelectedOnly(prev => !prev)
                setSearch("")
              }}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ListChecks className="h-3.5 w-3.5" />
              {selectedOnly ? (
                <>Show all {options.length}</>
              ) : (
                <>Show selected ({effectiveCount})</>
              )}
            </button>
            {selectedOnly && (
              <Badge variant="secondary" className="rounded-sm px-1.5 py-0 text-[10px] font-normal">
                Review mode
              </Badge>
            )}
          </div>
        )}
        <div
          className={`space-y-1 overflow-auto ${
            selectedOnly ? "max-h-[360px]" : "max-h-[260px]"
          }`}
        >
          {filtered.length === 0 ? (
            <p className="text-xs text-muted-foreground px-2 py-3 text-center">No results</p>
          ) : (
            <>
              {/* Select All */}
              <div
                className="flex items-center space-x-2 px-2 py-1.5 rounded-sm hover:bg-accent cursor-pointer border-b mb-1 pb-1.5"
                onClick={handleSelectAll}
              >
                <Checkbox
                  checked={(() => {
                    // Ignore zero-count / disabled rows here, mirroring
                    // handleSelectAll. Otherwise the checkbox sits at
                    // "indeterminate" forever (the disabled rows can
                    // never be selected) and toggling it appears to do
                    // nothing.
                    const selectable = filtered.filter(
                      o => !o.disabled && o.count !== 0,
                    )
                    if (selectable.length === 0) return false
                    if (selectable.every(o => effectiveSelected.has(o.value))) return true
                    if (selectable.some(o => effectiveSelected.has(o.value))) return "indeterminate"
                    return false
                  })()}
                  onCheckedChange={handleSelectAll}
                  onClick={(e) => e.stopPropagation()}
                />
                <span className="text-sm flex-1 font-medium">Select All</span>
              </div>
              {filtered.map(option => {
                const isDisabled = option.disabled || option.count === 0
                return (
                  <div
                    key={option.value}
                    title={isDisabled ? 'No recipients in this status' : undefined}
                    className={`flex items-center space-x-2 px-2 py-1.5 rounded-sm ${
                      isDisabled
                        ? 'opacity-40 cursor-not-allowed'
                        : 'hover:bg-accent cursor-pointer'
                    }`}
                    onClick={() => !isDisabled && handleToggle(option.value)}
                  >
                    <Checkbox
                      checked={effectiveSelected.has(option.value)}
                      disabled={isDisabled}
                      onCheckedChange={() => !isDisabled && handleToggle(option.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="text-sm flex-1 truncate">{option.label}</span>
                    {option.count !== undefined && (
                      <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                        ({option.count.toLocaleString()})
                      </span>
                    )}
                  </div>
                )
              })}
            </>
          )}
        </div>
        {selectedValues.length > 0 && (
          <div className="border-t mt-2 pt-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-8 text-xs"
              onClick={() => onSelectionChange([])}
            >
              Clear filters
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
    {!open && (selectedValues.length > 0 ? (
      <TooltipContent
        side="bottom"
        align="start"
        collisionPadding={12}
        avoidCollisions
        className={`p-0 ${useTwoColumns ? "max-w-[480px]" : "max-w-[260px]"}`}
      >
        <div className="px-3 py-2 border-b border-border/50">
          <p className="text-xs font-semibold">
            {title}
            <span className="ml-1.5 font-normal text-muted-foreground">
              ({effectiveCount} selected)
            </span>
          </p>
        </div>
        <ul
          className={`px-3 py-2 max-h-[320px] overflow-y-auto ${
            useTwoColumns ? "columns-2 gap-x-4" : "space-y-0.5"
          }`}
        >
          {previewLabels.map((label, i) => (
            <li
              key={`${label}-${i}`}
              className={`text-xs truncate ${useTwoColumns ? "break-inside-avoid py-0.5" : ""}`}
            >
              {label}
            </li>
          ))}
        </ul>
        <div className="px-3 py-2 border-t border-primary-foreground/20 text-[11px] flex items-center gap-1.5 bg-primary-foreground/10 rounded-b-md">
          <ListChecks className="h-3.5 w-3.5 shrink-0" />
          {hiddenPreviewCount > 0 ? (
            <span>
              <span className="font-semibold">+{hiddenPreviewCount} more</span>
              {" — click the "}
              <span className="font-semibold underline underline-offset-2">{title}</span>
              {" chip to review or edit all"}
            </span>
          ) : (
            <span>
              {"Click the "}
              <span className="font-semibold underline underline-offset-2">{title}</span>
              {" chip to edit selections"}
            </span>
          )}
        </div>
      </TooltipContent>
    ) : description ? (
      <TooltipContent side="bottom" className="max-w-[300px]">
        {description}
      </TooltipContent>
    ) : null)}
      </Tooltip>
    </TooltipProvider>
  )
}

/**
 * Compact segmented toggle that flips the global search mode between
 * "exact" (word-boundary) and "fuzzy" (subsequence).
 */
function SearchModeToggle({
  mode,
  onChange,
}: {
  mode: SearchMode
  onChange: (next: SearchMode) => void
}) {
  return (
    <TooltipProvider delayDuration={300}>
      <div
        role="group"
        aria-label="Search mode"
        className="flex items-center rounded-md border border-input bg-background p-0.5 shrink-0"
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => onChange("exact")}
              aria-pressed={mode === "exact"}
              className={`px-2 h-6 text-[10px] font-medium rounded-[4px] transition-colors ${
                mode === "exact"
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Exact
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[240px] text-xs">
            <span className="font-semibold">Exact</span> — word-boundary match.
            <br />
            Typing &quot;AI&quot; finds &quot;AI Officer&quot; but not &quot;Chair&quot;.
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => onChange("fuzzy")}
              aria-pressed={mode === "fuzzy"}
              className={`px-2 h-6 text-[10px] font-medium rounded-[4px] transition-colors ${
                mode === "fuzzy"
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Fuzzy
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[240px] text-xs">
            <span className="font-semibold">Fuzzy</span> — match characters in order, skip gaps.
            <br />
            &quot;snrengmgr&quot; finds &quot;Senior Engineering Manager&quot;.
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  )
}
